import { useState } from "react";
import axios from "axios";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { format } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppointmentSection = ({ services }) => {
  const { t, language } = useLanguage();
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    time: "",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timeSlots = [
    "08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM",
    "12:00 PM", "01:00 PM", "02:00 PM", "03:00 PM",
    "04:00 PM", "05:00 PM", "06:00 PM"
  ];

  const serviceOptions = [
    { en: "Emergency Plumbing", es: "Plomería de Emergencia" },
    { en: "Pipe Repair & Replacement", es: "Reparación de Tuberías" },
    { en: "Water Heater Installation", es: "Instalación de Calentadores" },
    { en: "Drain Cleaning", es: "Destapado de Drenajes" },
    { en: "Residential Plumbing", es: "Plomería Residencial" }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error(language === 'en' ? 'Please select a date' : 'Por favor selecciona una fecha');
      return;
    }

    if (!formData.time) {
      toast.error(language === 'en' ? 'Please select a time' : 'Por favor selecciona una hora');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const appointmentData = {
        ...formData,
        date: format(date, "yyyy-MM-dd"),
        language
      };
      
      await axios.post(`${API}/appointments`, appointmentData);
      
      toast.success(language === 'en' ? 'Appointment scheduled! We will contact you soon.' : '¡Cita agendada! Te contactaremos pronto.');
      
      setFormData({
        name: "",
        phone: "",
        email: "",
        service: "",
        time: "",
        notes: ""
      });
      setDate(null);
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error(language === 'en' ? 'Error scheduling appointment. Please try again.' : 'Error al agendar. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="citas" className="py-10 md:py-14 bg-white" data-testid="appointment-section">
      <div className="px-4 md:px-8 lg:px-16">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0F172A] rounded-full mb-3">
              <CalendarDays className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="appointment-title">
              {language === 'en' ? 'Schedule Appointment' : 'Agendar Cita'}
            </h2>
          </div>

          {/* Form - Mobile Optimized */}
          <form onSubmit={handleSubmit} className="bg-slate-50 p-5 md:p-6 rounded-xl space-y-4" data-testid="appointment-form">
            {/* NAME */}
            <div>
              <Label htmlFor="appt-name" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Name' : 'Nombre'} *
              </Label>
              <Input
                id="appt-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={language === 'en' ? 'Your full name' : 'Tu nombre completo'}
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12"
                data-testid="appointment-name-input"
              />
            </div>

            {/* PHONE */}
            <div>
              <Label htmlFor="appt-phone" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Phone' : 'Teléfono'} *
              </Label>
              <Input
                id="appt-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(123) 456-7890"
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12"
                data-testid="appointment-phone-input"
              />
            </div>

            {/* EMAIL - Optional */}
            <div>
              <Label htmlFor="appt-email" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Email' : 'Correo'} <span className="text-slate-400 font-normal">({language === 'en' ? 'optional' : 'opcional'})</span>
              </Label>
              <Input
                id="appt-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12"
                data-testid="appointment-email-input"
              />
            </div>

            {/* SERVICE */}
            <div>
              <Label htmlFor="appt-service" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Service' : 'Servicio'} *
              </Label>
              <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })} required>
                <SelectTrigger className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12" data-testid="appointment-service-select">
                  <SelectValue placeholder={language === 'en' ? 'Select a service' : 'Selecciona un servicio'} />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((service, idx) => (
                    <SelectItem key={idx} value={service.en}>
                      {language === 'en' ? service.en : service.es}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* DATE & TIME - Side by side on larger screens */}
            <div className="grid grid-cols-2 gap-3">
              {/* DATE */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                  {language === 'en' ? 'Date' : 'Fecha'} *
                </Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full h-12 flex items-center justify-between px-3 border border-slate-200 rounded-md bg-white hover:border-[#D4AF37] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors text-sm"
                      data-testid="appointment-date-picker"
                    >
                      <span className={date ? "text-[#0F172A]" : "text-slate-400"}>
                        {date ? format(date, "MM/dd") : (language === 'en' ? 'Select' : 'Elegir')}
                      </span>
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(selectedDate) => {
                        setDate(selectedDate);
                        setDateOpen(false);
                      }}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* TIME */}
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                  {language === 'en' ? 'Time' : 'Hora'} *
                </Label>
                <Select value={formData.time} onValueChange={(value) => setFormData({ ...formData, time: value })} required>
                  <SelectTrigger className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12" data-testid="appointment-time-select">
                    <SelectValue placeholder={language === 'en' ? 'Select' : 'Elegir'} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* NOTES - Optional */}
            <div>
              <Label htmlFor="appt-notes" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Notes' : 'Notas'} <span className="text-slate-400 font-normal">({language === 'en' ? 'optional' : 'opcional'})</span>
              </Label>
              <Textarea
                id="appt-notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={2}
                placeholder={language === 'en' ? 'Any additional details...' : 'Detalles adicionales...'}
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                data-testid="appointment-notes-textarea"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#0F172A] text-white px-6 py-4 text-sm uppercase tracking-widest font-semibold hover:bg-[#1E293B] transition-all duration-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="appointment-submit-button"
            >
              {isSubmitting 
                ? (language === 'en' ? 'Scheduling...' : 'Agendando...') 
                : (language === 'en' ? 'Schedule Appointment' : 'Agendar Cita')}
            </button>

            {/* Contact Notice */}
            <p className="text-center text-xs text-slate-500 pt-2">
              {language === 'en' 
                ? 'We will contact you by phone or email within 24 hours.' 
                : 'Te contactaremos por teléfono o email dentro de 24 horas.'}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AppointmentSection;
